import {combineLatest as observableCombineLatest, of as observableOf, Observable} from 'rxjs';

import {first, map} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnDestroy,
} from '@angular/core';
import {AbstractControlComponent} from './abstract-control';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ViewTabComponent} from '../viewtab.component';
import {SearchScopeConfiguration} from '../../../cache/uiconfiguration/searchscopeconfiguration';
import {CacheService} from '../../../cache/cache.service';
import {LanguageService} from '../../../language/language.service';
import {ErrorService} from '../../../error/error.service';
import {FilterbuilderDialogComponent} from '../../../shared/filterbuilder/filterbuilder-dialog.component';
import {ObjectTypeDescription} from '../../../cache/schema/objecttypedescription';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';

@Component({
  selector: 'ctrl-filterbuilder',
  template: `
    <p>{{ text | async }}</p>
    @if (getCaughtObservable(isEnabled) | async) {
      <div>
        <button mat-raised-button color="primary" (click)="onManageClick()">
          <mat-icon>tune</mat-icon>
          <language key="CtrlFilterbuilderManage" defaultValue="manage"></language>
        </button>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    ':host {display: flex; flex-direction: row; justify-content: space-between; align-items: center;} p { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis;} div { flex: 0 0 auto; } button {text-transform: uppercase; } ',
  ],
  standalone: false,
})
export class CtrlFilterbuilderComponent extends AbstractControlComponent implements OnDestroy {
  @Input() objectType: string = null;
  @Input() objectID: string = null;
  @Input() objectDisplayName: string = null;

  constructor(
    parent: ViewTabComponent,
    private dialog: MatDialog,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    private cacheService: CacheService,
    errorService: ErrorService,
    private envConfig: EnvironmentConfigService,
  ) {
    super(parent, 'Value', 'Value', changeDetector, _ngZone, errorService);
  }

  protected formatTarget(value: any): any {
    return value;
  }

  protected formatSource(source: any): any {
    return source;
  }

  get text(): Observable<string> {
    if (this.formControl.value == null || this.formControl.value.trim().length === 0)
      return observableOf('');
    const matches = /^\/(\*|[a-zA-Z_:][a-zA-Z0-9\-._:]*)(\[.+\])?/.exec(this.formControl.value);
    if (matches == null || matches.length < 1) return observableOf('');
    if (matches[1] === '*') {
      return this.cacheService.getLanguageResource().pipe(
        map(lr => {
          const dn = LanguageService.translate('FilterbuilderAllResources', 'All Resources', lr);
          if (matches[2] != undefined) {
            return LanguageService.translate(
              'CtrlFilterbuilderTextWithFilter',
              '{0} with Filter',
              lr,
              [dn],
            );
          } else {
            return dn;
          }
        }),
      );
    } else if (matches[2] == undefined) {
      return this.cacheService.getResources().pipe(
        map((otds: ObjectTypeDescription[]) => {
          for (let i = 0; i < otds.length; i++) {
            if (otds[i].systemname === matches[1]) {
              return otds[i].displayname;
            }
          }
          return '';
        }),
      );
    } else {
      return observableCombineLatest(
        this.cacheService.getResources(),
        this.cacheService.getLanguageResource(),
        (otds: ObjectTypeDescription[], lr) => {
          for (let i = 0; i < otds.length; i++) {
            if (otds[i].systemname === matches[1]) {
              return LanguageService.translate(
                'CtrlFilterbuilderTextWithFilter',
                '{0} with Filter',
                lr,
                [otds[i].displayname],
              );
            }
          }
          return '';
        },
      );
    }
  }

  private onManageClick() {
    let dialogRef: MatDialogRef<FilterbuilderDialogComponent> = this.dialog.open(
      FilterbuilderDialogComponent,
    );

    if (this._config.propertyExists('PermittedObjectTypes')) {
      this._config.getPropertyValues('PermittedObjectTypes').subscribe(
        objects =>
          (dialogRef.componentInstance.permittedObjects = objects
            .split(',')
            .map(s => s.trim())
            .filter(s => s)),
      );
    }
    if (this._config.propertyExists('PermittedAttributeTypes')) {
      this._config.getPropertyValues('PermittedAttributeTypes').subscribe(
        attributes =>
          (dialogRef.componentInstance.permittedAttributes = attributes
            .split(',')
            .map(s => s.trim())
            .filter(s => s)),
      );
    }
    if (this._config.propertyExists('AllEligibleResourcesFilterOption')) {
      this._config
        .getBooleanPropertyValues('AllEligibleResourcesFilterOption')
        .subscribe(
          value => (dialogRef.componentInstance.showAllEligibleResourcesFilterOption = value),
        );
    }

    if (this.formControl.value != null && this.formControl.value.trim().length !== 0) {
      dialogRef.componentInstance.searchScope = <SearchScopeConfiguration>{
        id: 'ctrl-filterbuilder-ssc',
        displayname: 'Filter Builder Control',
        description: 'Filter Builder Control',
        usagekeyword: [],
        order: 0,
        attributesearched: this.envConfig.getAttributeName('DisplayName')
          ? [this.envConfig.getAttributeName('DisplayName')]
          : [],
        xpath: this.formControl.value,
        resultobjecttype: null, // set by parsing xpath
        attributedisplayed: this.envConfig.getAttributeName('DisplayName')
          ? [this.envConfig.getAttributeName('DisplayName')]
          : [],
      };
    }
    this._config.Caption.pipe(first()).subscribe(
      c => (dialogRef.componentInstance.title = c ? c.Values : ''),
    );
    dialogRef
      .afterClosed()
      .pipe(first())
      .subscribe((sc: SearchScopeConfiguration | boolean) => {
        if (sc === false) {
          this.formControl.setValue(null, {emitEvent: true});
          this.changeDetector.markForCheck();
        } else if (sc != null && sc !== true) {
          this.formControl.setValue(sc.xpath, {emitEvent: true});
          this.changeDetector.markForCheck();
        }
      });
  }
}
